import { ChevronRightIcon } from '@chakra-ui/icons';
import { Center, Divider, Flex, Heading, Spacer } from '@chakra-ui/react';
import { IconArrowLeft } from 'central-icons/IconArrowLeft';
import type { ReactNode } from 'react';
import React, { isValidElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-use';

import { LoadingBox } from '@revelio/core';
import { Button } from '@revelio/ui';

import { usePageLoadMonitor } from '../../hooks/usePageLoadMonitor';
import { useTutorialLinkHandler } from '../../hooks/useTutorialLinkHandler';
import { RlBox } from '../box/box';
import { TabsList, TabsPanel } from '../tabs';
import styles from './page.module.css';

const PAGE_CONTAINER_CLASS_NAME = 'revelio-page-container-class-name';

export type TitleType = string | string[];

export interface PageProps {
  title: TitleType;
  backPath?: string;
  topRight?: React.ReactNode;
  selections?: JSX.Element; //this will be the little chips that show what youve selected
  hideSelectionsMargins?: boolean;
  hideTop?: boolean;
  display?: 'flex' | 'block';
  children: React.ReactNode;
  loading?: boolean;
  scrollY?: boolean;
}

function headerTemplate(title: TitleType) {
  // biome-ignore lint/suspicious/noImplicitAnyLet: migrated from develop
  let arrayCopy;

  if (!Array.isArray(title)) {
    arrayCopy = [title];
  } else {
    arrayCopy = [...title];
  }

  if (arrayCopy.length === 0) {
    throw new ReferenceError(
      'Title must be a string or a non-empty array of strings'
    );
  }

  return (
    <Flex direction="row" data-testid="page-header-label">
      <Heading color="text.primary" fontWeight="600" fontSize="xl">
        {arrayCopy.shift()}
      </Heading>
      {arrayCopy.map((item, i) => {
        return (
          <Center key={i}>
            <ChevronRightIcon
              boxSize="5"
              color="text.primary"
              marginX="3px"
              transform="translateY(1px)"
            />
            <Heading color="text.primary" fontWeight="600" fontSize="xl">
              {item}
            </Heading>
          </Center>
        );
      })}
    </Flex>
  );
}

export function Page({
  topRight,
  backPath,
  selections,
  hideSelectionsMargins,
  hideTop,
  display = 'flex',
  loading,
  scrollY,
  ...props
}: PageProps) {
  const navigate = useNavigate();
  const originalChildren = React.Children.toArray(props.children);

  const children: {
    filtersContainer: React.ReactElement[];
    tabs: React.ReactElement[];
    other: ReactNode[];
  } = {
    filtersContainer: [],
    tabs: [],
    other: [],
  };
  const tabNames: string[] = [];
  let tabsEnabled = false;

  originalChildren.forEach((tItem) => {
    if (isValidElement(tItem)) {
      children.other.push(tItem);
    }
  });

  tabsEnabled = children.tabs.length > 0;

  const [tab, setTab] = useState<string>(tabNames[0] ?? '');

  const pageTitle = Array.isArray(props.title)
    ? props.title.join(' > ')
    : props.title;
  usePageLoadMonitor(pageTitle, loading);

  const location = useLocation();

  useTutorialLinkHandler(location);

  return (
    <Flex
      backgroundColor="background.primary"
      height="100vh"
      direction="column"
      paddingX="6"
      paddingTop="2"
      paddingBottom="1"
      overflowX="hidden"
      overflowY={hideTop || scrollY ? 'auto' : 'hidden'}
      display={display}
      className={PAGE_CONTAINER_CLASS_NAME}
    >
      {!hideTop && (
        <>
          <Flex direction="row" flexWrap="wrap">
            <Center justifyContent="flex-start">
              {backPath && (
                <Button
                  aria-label="Go back"
                  leftIcon={<IconArrowLeft size={16} />}
                  size="xs"
                  className={styles['backButton']}
                  onClick={() => navigate(backPath)}
                />
              )}
              {headerTemplate(props.title)}
            </Center>
            <Spacer />
            <Center justifyContent="flex-end" gap={2}>
              {topRight}
            </Center>
          </Flex>
          <Divider
            color="text.lowContrast"
            marginTop="1"
            marginBottom={hideSelectionsMargins ? '0' : '3'}
            opacity="1"
          />
        </>
      )}

      {selections && (
        <Flex direction="row" marginBottom={hideSelectionsMargins ? '0' : '3'}>
          {selections}
        </Flex>
      )}

      <Flex /*height="12"*/ direction="row" marginBottom="2">
        {!!children.filtersContainer.length && (
          <Center flexGrow={2} height="100%" justifyContent="flex-start">
            {children.filtersContainer}
          </Center>
        )}

        {tabNames.length > 0 && (
          <Center height="100%" justifyContent="flex-end">
            <TabsList tabs={tabNames} tab={tab} setTab={setTab} />
          </Center>
        )}
      </Flex>

      <LoadingBox
        testId="page-data-loading"
        loading={loading}
        backgroundColorOpacity={1}
        height={{ base: 'unset', sm: 'fit-content' }}
        overflowY={{ base: 'scroll', md: 'unset' }}
      >
        {tabsEnabled ? (
          <TabsPanel tab={tab}>{children.tabs}</TabsPanel>
        ) : (
          <RlBox variant="large-container">{children.other}</RlBox>
        )}
      </LoadingBox>
    </Flex>
  );
}

export default Page;
